import { Deferred, List, pipe } from '@helo/utils';
import { DataTypeConfigU } from '../DateTypeConfig/DataTypeConfig';
import { DataTypeConfigUtils } from '../DateTypeConfig/DataTypeConfig.utils';
import { DataTypeSelection } from '../DateTypeConfig/DataTypeSelection';
import { ContentNodeUtils, createContentNode, findContentNodeCached, } from './ContentNode.utils';
const createMappableSourceTypeSet = (mappers) => {
    const sourceTypes = new Set();
    for (const mapper of mappers) {
        sourceTypes.add(mapper.sourceType);
    }
    return sourceTypes;
};
const mappableSourceTypeSetCache = new WeakMap();
const getMappableSourceTypeSet = (mappers) => {
    const cachedSourceTypes = mappableSourceTypeSetCache.get(mappers);
    if (cachedSourceTypes) {
        return cachedSourceTypes;
    }
    const sourceTypes = createMappableSourceTypeSet(mappers);
    mappableSourceTypeSetCache.set(mappers, sourceTypes);
    return sourceTypes;
};
const containsMappableTypesFromSourceTypes = (sourceTypes, node) => {
    switch (node.kind) {
        case 'OBJECT':
        case 'INPUT_OBJECT': {
            if (sourceTypes.has(node.typeName)) {
                return true;
            }
            for (const field of node.fields) {
                if (containsMappableTypesFromSourceTypes(sourceTypes, field)) {
                    return true;
                }
            }
            return false;
        }
        case 'FIELD':
        case 'INPUT_VALUE':
            return containsMappableTypesFromSourceTypes(sourceTypes, node.ofType);
        case 'LIST': {
            if (containsMappableTypesFromSourceTypes(sourceTypes, node.ofType)) {
                return true;
            }
            for (const item of node.items ?? []) {
                if (containsMappableTypesFromSourceTypes(sourceTypes, item)) {
                    return true;
                }
            }
            return false;
        }
        case 'ENUM':
        case 'ENUM_VALUE':
        case 'SCALAR':
        default:
            return false;
    }
};
export const containsMappableTypes = (mappers, node) => {
    const sourceTypes = getMappableSourceTypeSet(mappers);
    if (sourceTypes.size === 0) {
        return false;
    }
    return containsMappableTypesFromSourceTypes(sourceTypes, node);
};
export const mapContentNode = (dataTypeConfigs, mappers) => {
    const mapNode = (node) => {
        if (!containsMappableTypes(mappers, node)) {
            return node;
        }
        const mapFn = mapNode;
        if (node.kind === 'OBJECT') {
            const mapper = mappers.find(m => m.sourceType === node.typeName);
            if (!mapper) {
                const mappedFields = node.fields.map(mapFn);
                const data = node.data &&
                    mappedFields.reduce((acc, field) => {
                        return {
                            ...acc,
                            [field.key]: field.data,
                        };
                    }, {});
                return {
                    ...node,
                    fields: mappedFields,
                    data,
                };
            }
            if (mapper.kind !== 'OBJECT_MAPPER') {
                throw new Error(`Mapper for OBJECT "${node.typeName}" must be an OBJECT_MAPPER, but found ${mapper.kind}`);
            }
            const destinationConfig = dataTypeConfigs.find(c => c.kind === 'OBJECT' && c.typeName === mapper.destinationType);
            if (!destinationConfig) {
                throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
            }
            if (destinationConfig.kind !== 'OBJECT') {
                throw new Error(`Destination type "${mapper.destinationType}" is not an object. It is a ${destinationConfig.kind}`);
            }
            const extractorCache = {};
            const mappedFields = mapper.fields
                .map(fieldMapper => {
                const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
                if (!destField) {
                    return null;
                }
                try {
                    const locatedNode = findContentNodeCached(fieldMapper.locator, extractorCache)(node);
                    if (!locatedNode) {
                        return null;
                    }
                    const ofType = locatedNode.kind === 'FIELD'
                        ? mapFn(locatedNode.ofType)
                        : mapFn(locatedNode);
                    const fieldConfig = DataTypeConfigU.createFieldDataTypeConfig({
                        ...destField,
                        key: fieldMapper.key,
                        ofType: ofType.getDataTypeConfig(),
                        state: pipe(locatedNode.state ?? {}, Deferred.merge(destField.state ?? {}), Deferred.merge(fieldMapper.state ?? {})),
                        meta: {
                            path: [
                                ['typeName', mapper.destinationType],
                                ['key', fieldMapper.key],
                            ],
                            unmappedPath: locatedNode.meta?.unmappedPath ??
                                locatedNode.meta?.path ??
                                [],
                        },
                    });
                    return createContentNode(fieldConfig, ofType.data);
                }
                catch (error) {
                    console.error(error);
                    return null;
                }
            })
                .filter((field) => field !== null);
            const data = node.data &&
                mappedFields.reduce((acc, field) => {
                    return {
                        ...acc,
                        [field.key]: field.data,
                    };
                }, {});
            const objectConfig = DataTypeConfigU.createObjectDataTypeConfig({
                ...destinationConfig,
                fields: mappedFields.map(f => f.getDataTypeConfig()),
                state: pipe(destinationConfig.state ?? {}, Deferred.merge(node.state ?? {}), Deferred.merge(mapper.state ?? {})),
                meta: {
                    path: [['typeName', mapper.destinationType]],
                    unmappedPath: node.meta?.unmappedPath ?? node.meta?.path ?? [],
                },
            });
            return createContentNode(objectConfig, data);
        }
        switch (node.kind) {
            case 'FIELD':
                const mappedOfType = mapFn(node.ofType);
                return {
                    ...node,
                    ofType: mappedOfType,
                    data: mappedOfType.data,
                };
            case 'LIST': {
                const mappedOfType = DataTypeConfigUtils.mapDataTypeConfig(dataTypeConfigs, mappers)(node.ofType);
                const mappedItems = node.items?.map(mapFn);
                return {
                    ...node,
                    ofType: mappedOfType,
                    items: mappedItems,
                    data: mappedItems?.map(item => item?.data),
                };
            }
            case 'INPUT_OBJECT': {
                const mapper = mappers.find(m => m.sourceType === node.typeName);
                if (!mapper) {
                    const mappedInputFields = node.fields.map(mapFn);
                    const data = node.data &&
                        mappedInputFields.reduce((acc, field) => {
                            return {
                                ...acc,
                                [field.key]: field.data,
                            };
                        }, {});
                    return {
                        ...node,
                        fields: mappedInputFields,
                        data,
                    };
                }
                if (mapper.kind !== 'INPUT_OBJECT_MAPPER') {
                    throw new Error(`Mapper for INPUT_OBJECT "${node.typeName}" must be an INPUT_OBJECT_MAPPER, but found ${mapper.kind}`);
                }
                const destinationConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.destinationType);
                if (!destinationConfig) {
                    throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
                }
                if (destinationConfig.kind !== 'INPUT_OBJECT') {
                    throw new Error(`Destination type "${mapper.destinationType}" is not an input object. It is a ${destinationConfig.kind}`);
                }
                const extractorCache = {};
                const mappedInputFields = mapper.fields
                    .map(inputValueMapper => {
                    const destField = destinationConfig.fields.find(f => f.key === inputValueMapper.key);
                    if (!destField) {
                        console.error('input field not in destination', inputValueMapper.key);
                        return null;
                    }
                    try {
                        const locatedNode = findContentNodeCached(inputValueMapper.locator, extractorCache)(node);
                        if (!locatedNode) {
                            return null;
                        }
                        const ofType = locatedNode.kind === 'INPUT_VALUE'
                            ? mapFn(locatedNode.ofType)
                            : mapFn(locatedNode);
                        const inputConfig = DataTypeConfigU.createInputValueDataTypeConfig({
                            ...destField,
                            key: inputValueMapper.key,
                            ofType: ofType.getDataTypeConfig(),
                            state: pipe(locatedNode.state ?? {}, Deferred.merge(destField.state ?? {}), Deferred.merge(inputValueMapper.state ?? {})),
                            meta: {
                                path: [
                                    ['typeName', mapper.destinationType],
                                    ['key', inputValueMapper.key],
                                ],
                                unmappedPath: locatedNode.meta?.unmappedPath ??
                                    locatedNode.meta?.path ??
                                    [],
                            },
                        });
                        return createContentNode(inputConfig, ofType.data);
                    }
                    catch (error) {
                        console.error(error);
                        return null;
                    }
                })
                    .filter((field) => field !== null);
                const data = node.data &&
                    mappedInputFields.reduce((acc, field) => {
                        return {
                            ...acc,
                            [field.key]: field.data,
                        };
                    }, {});
                const inputObjectConfig = DataTypeConfigU.createInputObjectDataTypeConfig({
                    ...destinationConfig,
                    fields: mappedInputFields.map(f => f.getDataTypeConfig()),
                    state: pipe(destinationConfig.state ?? {}, Deferred.merge(node.state ?? {}), Deferred.merge(mapper.state ?? {})),
                    meta: {
                        path: [['typeName', mapper.destinationType]],
                        unmappedPath: node.meta?.unmappedPath ?? node.meta?.path ?? [],
                    },
                });
                return createContentNode(inputObjectConfig, data);
            }
            case 'INPUT_VALUE':
                const mappedInputValue = mapFn(node.ofType);
                return {
                    ...node,
                    ofType: mappedInputValue,
                    data: mappedInputValue.data,
                };
            case 'ENUM':
            case 'ENUM_VALUE':
            case 'SCALAR':
            default:
                return node;
        }
    };
    return mapNode;
};
export const reverseMapContentNode = (dataTypeConfigs, mappers) => (destinationContentNode) => {
    const mapper = mappers.find(m => m.destinationType === destinationContentNode.typeName);
    if (!mapper) {
        switch (destinationContentNode.kind) {
            case 'OBJECT':
            case 'INPUT_OBJECT':
                return pipe(destinationContentNode, ContentNodeUtils.updateChildContentNode(pipe(destinationContentNode.fields, List.map(reverseMapContentNode(dataTypeConfigs, mappers)))));
            case 'FIELD':
            case 'INPUT_VALUE':
                return ContentNodeUtils.updateChildContentNode(reverseMapContentNode(dataTypeConfigs, mappers)(destinationContentNode.ofType))(destinationContentNode);
            case 'LIST':
                return ContentNodeUtils.updateChildContentNode(destinationContentNode.items?.map(reverseMapContentNode(dataTypeConfigs, mappers)))(destinationContentNode);
            case 'ENUM':
            case 'ENUM_VALUE':
            case 'SCALAR':
            default:
                return destinationContentNode;
        }
    }
    const sourceConfig = dataTypeConfigs.find(c => c.kind === destinationContentNode.kind &&
        c.typeName === mapper?.sourceType);
    if (!sourceConfig) {
        throw new Error(`Source type "${destinationContentNode.typeName}" not found in data type configs`);
    }
    const sourceConfigSelection = DataTypeSelection.createSelectionFromLocators(mapper.fields.map(field => field.locator));
    const selectedSourceConfig = DataTypeSelection.getSelection(sourceConfigSelection)(sourceConfig);
    const sourceContentNode = ContentNodeUtils.createContentNode(selectedSourceConfig, undefined);
    const updatedSourceContentNode = mapper.fields.reduce((acc, field) => {
        const destinationField = ContentNodeUtils.findContentNode([
            ['key', field.key],
        ])(destinationContentNode);
        if (!destinationField) {
            return acc;
        }
        const targetedSourceContentNode = ContentNodeUtils.findContentNode(field.locator)(sourceContentNode);
        if (!targetedSourceContentNode) {
            return acc;
            throw new Error(`destination field "${field.key}" not found in source content node at locator ${JSON.stringify(field.locator)}`);
        }
        const newSourceFieldContentNode = ContentNodeUtils.createContentNode(targetedSourceContentNode, destinationField.data);
        return ContentNodeUtils.findAndUpdateChild(field.locator, (child) => {
            return newSourceFieldContentNode;
        })(acc);
    }, sourceContentNode);
    return updatedSourceContentNode;
};
const locateMappedNode = (fieldMapper, sourceNode) => {
    return findContentNodeCached(fieldMapper.locator)(sourceNode);
};
const applySingleObjectMapper = (dataTypeConfigs, mapper, sharedExtractorCache) => (node) => {
    if (node.kind !== 'OBJECT') {
        return node;
    }
    const destinationConfig = dataTypeConfigs.find(c => c.kind === 'OBJECT' && c.typeName === mapper.destinationType);
    if (!destinationConfig || destinationConfig.kind !== 'OBJECT') {
        console.warn(`Destination type "${mapper.destinationType}" not found or is not an object`);
        return node;
    }
    const extractorCache = sharedExtractorCache ?? {};
    const dataCache = {};
    const mappedFields = mapper.fields
        .map(fieldMapper => {
        const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
        if (!destField) {
            return null;
        }
        try {
            const locatedNode = findContentNodeCached(fieldMapper.locator, extractorCache, dataCache)(node);
            if (!locatedNode) {
                return null;
            }
            const ofType = locatedNode.kind === 'FIELD'
                ? locatedNode.ofType
                : locatedNode;
            const fieldConfig = DataTypeConfigU.createFieldDataTypeConfig({
                ...destField,
                key: fieldMapper.key,
                ofType: ofType.getDataTypeConfig(),
                state: pipe(locatedNode.state ?? {}, Deferred.merge(destField.state ?? {}), Deferred.merge(fieldMapper.state ?? {})),
                meta: {
                    path: [
                        ['typeName', mapper.destinationType],
                        ['key', fieldMapper.key],
                    ],
                    unmappedPath: locatedNode.meta?.unmappedPath ?? locatedNode.meta?.path ?? [],
                },
            });
            return createContentNode(fieldConfig, ofType.data);
        }
        catch {
            return null;
        }
    })
        .filter((field) => field !== null);
    const data = node.data &&
        mappedFields.reduce((acc, field) => {
            acc[field.key] = field.data;
            return acc;
        }, {});
    const objectConfig = DataTypeConfigU.createObjectDataTypeConfig({
        ...destinationConfig,
        fields: mappedFields.map(f => f.getDataTypeConfig()),
        state: pipe(destinationConfig.state ?? {}, Deferred.merge(node.state ?? {}), Deferred.merge(mapper.state ?? {})),
        meta: {
            path: [['typeName', mapper.destinationType]],
            unmappedPath: node.meta?.unmappedPath ?? node.meta?.path ?? [],
        },
    });
    return createContentNode(objectConfig, data);
};
const applySingleInputObjectMapper = (dataTypeConfigs, mapper) => (node) => {
    if (node.kind !== 'INPUT_OBJECT') {
        return node;
    }
    const destinationConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.destinationType);
    if (!destinationConfig || destinationConfig.kind !== 'INPUT_OBJECT') {
        console.warn(`Destination type "${mapper.destinationType}" not found or is not an input object`);
        return node;
    }
    const extractorCache = {};
    const mappedFields = mapper.fields
        .map(fieldMapper => {
        const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
        if (!destField) {
            return null;
        }
        try {
            const locatedNode = findContentNodeCached(fieldMapper.locator, extractorCache)(node);
            if (!locatedNode) {
                return null;
            }
            const ofType = locatedNode.kind === 'INPUT_VALUE'
                ? locatedNode.ofType
                : locatedNode;
            const inputValueConfig = DataTypeConfigU.createInputValueDataTypeConfig({
                ...destField,
                key: fieldMapper.key,
                ofType: ofType.getDataTypeConfig(),
                state: pipe(locatedNode.state ?? {}, Deferred.merge(destField.state ?? {}), Deferred.merge(fieldMapper.state ?? {})),
                meta: {
                    path: [
                        ['typeName', mapper.destinationType],
                        ['key', fieldMapper.key],
                    ],
                    unmappedPath: locatedNode.meta?.unmappedPath ??
                        locatedNode.meta?.path ??
                        [],
                },
            });
            return createContentNode(inputValueConfig, ofType.data);
        }
        catch (error) {
            return null;
        }
    })
        .filter((field) => field !== null);
    const data = node.data &&
        mappedFields.reduce((acc, field) => {
            return {
                ...acc,
                [field.key]: field.data,
            };
        }, {});
    const inputObjectConfig = DataTypeConfigU.createInputObjectDataTypeConfig({
        ...destinationConfig,
        fields: mappedFields.map(f => f.getDataTypeConfig()),
        state: pipe(destinationConfig.state ?? {}, Deferred.merge(node.state ?? {}), Deferred.merge(mapper.state ?? {})),
        meta: {
            path: [['typeName', mapper.destinationType]],
            unmappedPath: node.meta?.unmappedPath ?? node.meta?.path ?? [],
        },
    });
    return createContentNode(inputObjectConfig, data);
};
export const applyContentMappersAtLocations = (dataTypeConfigs, contentMappers) => (rootNode) => {
    if (!contentMappers || contentMappers.length === 0) {
        return rootNode;
    }
    let result = rootNode;
    for (const contentMapper of contentMappers) {
        const { locator, mapper } = contentMapper;
        const targetNode = findContentNodeCached(locator)(result);
        if (!targetNode) {
            continue;
        }
        const objectMapperExtractorCache = {};
        const applyObjectMapper = applySingleObjectMapper(dataTypeConfigs, mapper, objectMapperExtractorCache);
        const mapListItems = (listNode) => {
            return listNode.items?.map(item => {
                if (item.kind === 'OBJECT' && item.typeName === mapper.sourceType) {
                    return applyObjectMapper(item);
                }
                return item;
            });
        };
        const applyMapperToNode = (node) => {
            if (node.kind === 'OBJECT' && node.typeName === mapper.sourceType) {
                return applyObjectMapper(node);
            }
            return node;
        };
        if (targetNode.kind === 'FIELD' &&
            targetNode.ofType?.kind === 'LIST') {
            const listNode = targetNode
                .ofType;
            const mappedItems = mapListItems(listNode);
            result = ContentNodeUtils.findAndUpdateChild(locator, (node) => {
                if (node.kind === 'FIELD') {
                    const fieldNode = node;
                    if (fieldNode.ofType?.kind === 'LIST') {
                        const updatedList = {
                            ...fieldNode.ofType,
                            items: mappedItems,
                            data: mappedItems?.map(item => item?.data),
                        };
                        return {
                            ...fieldNode,
                            ofType: updatedList,
                            data: updatedList.data,
                        };
                    }
                }
                return node;
            })(result);
        }
        else if (targetNode.kind === 'FIELD' &&
            targetNode.ofType?.kind === 'OBJECT') {
            const mappedOfType = applyMapperToNode(targetNode.ofType);
            result = ContentNodeUtils.findAndUpdateChild(locator, (node) => {
                if (node.kind === 'FIELD') {
                    return {
                        ...node,
                        ofType: mappedOfType,
                        data: mappedOfType.data,
                    };
                }
                return node;
            })(result);
        }
        else if (targetNode.kind === 'LIST') {
            const mappedItems = mapListItems(targetNode);
            result = ContentNodeUtils.findAndUpdateChild(locator, (node) => {
                if (node.kind === 'LIST') {
                    return {
                        ...node,
                        items: mappedItems,
                        data: mappedItems?.map(item => item?.data),
                    };
                }
                return node;
            })(result);
        }
        else if (targetNode.kind === 'OBJECT') {
            const mappedNode = applyMapperToNode(targetNode);
            result = ContentNodeUtils.findAndUpdateChild(locator, () => mappedNode)(result);
        }
    }
    return result;
};
export const applyVariableMappersAtLocations = (dataTypeConfigs, variableMappers) => (variableContentNodes) => {
    if (!variableMappers || variableMappers.length === 0) {
        return variableContentNodes;
    }
    return variableContentNodes.map(varNode => {
        const variableMapper = variableMappers.find(vm => {
            const [firstMatcher] = vm.locator;
            if (firstMatcher && firstMatcher[0] === 'key') {
                return 'key' in varNode && varNode.key === firstMatcher[1];
            }
            return false;
        });
        if (!variableMapper) {
            return varNode;
        }
        const { mapper } = variableMapper;
        const applyMapperToNode = (node) => {
            if (node.kind === 'INPUT_OBJECT' &&
                node.typeName === mapper.sourceType) {
                return applySingleInputObjectMapper(dataTypeConfigs, mapper)(node);
            }
            return node;
        };
        const mapListItems = (listNode) => {
            return listNode.items?.map(item => applyMapperToNode(item));
        };
        if (varNode.kind === 'INPUT_VALUE' &&
            varNode.ofType?.kind === 'LIST') {
            const listNode = varNode
                .ofType;
            const mappedItems = mapListItems(listNode);
            const updatedList = {
                ...listNode,
                items: mappedItems,
                data: mappedItems?.map(item => item?.data),
            };
            return {
                ...varNode,
                ofType: updatedList,
                data: updatedList.data,
            };
        }
        else if (varNode.kind === 'INPUT_VALUE' &&
            varNode.ofType?.kind === 'INPUT_OBJECT') {
            const mappedOfType = applyMapperToNode(varNode.ofType);
            return {
                ...varNode,
                ofType: mappedOfType,
                data: mappedOfType.data,
            };
        }
        else if (varNode.kind === 'LIST') {
            const mappedItems = mapListItems(varNode);
            return {
                ...varNode,
                items: mappedItems,
                data: mappedItems?.map(item => item?.data),
            };
        }
        else if (varNode.kind === 'INPUT_OBJECT') {
            return applyMapperToNode(varNode);
        }
        return varNode;
    });
};
export const ContentNodeMapper = {
    containsMappableTypes,
    mapContentNode,
    reverseMapContentNode,
    applyContentMappersAtLocations,
    applyVariableMappersAtLocations,
    applySingleObjectMapper,
    applySingleInputObjectMapper,
};
//# sourceMappingURL=ContentNode-mapper.js.map